import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, FileText, Home, Loader2, Inbox, Building2, Mail, User as UserIcon, Euro } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABELS = {
  bozza: { label: "Bozza", color: "bg-slate-100 text-slate-700 border-slate-300" },
  inviata: { label: "Inviata", color: "bg-sky-100 text-sky-800 border-sky-300" },
  in_verifica: { label: "In verifica", color: "bg-amber-100 text-amber-800 border-amber-300" },
  documenti_mancanti: { label: "Documenti mancanti", color: "bg-orange-100 text-orange-800 border-orange-300" },
  contratto_approvato: { label: "Firma abilitata", color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  approvata: { label: "Approvata", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  attestazione_inviata: { label: "Attestazione inviata", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  respinta: { label: "Respinta", color: "bg-red-100 text-red-800 border-red-300" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};

const fmtEuro = (n) => {
  if (n == null || isNaN(Number(n))) return "—";
  return "€ " + Number(n).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export default function AgenziaDashboard() {
  const nav = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const token = localStorage.getItem("cc_user_token");
  const agenziaNome = localStorage.getItem("cc_user_agenzia_nome") || "";
  const email = localStorage.getItem("cc_user_email") || "";

  useEffect(() => {
    if (!token) { nav("/agenzia/login"); return; }
    (async () => {
      try {
        // Verify this is really an agency account; otherwise redirect to the
        // regular locatore dashboard so the user doesn't see an empty panel.
        const me = await axios.get(`${API}/user-auth/me`, { headers: { Authorization: `Bearer ${token}` } });
        if ((me.data?.account_type || "").toLowerCase() !== "agenzia") {
          nav("/utente");
          return;
        }
        if (me.data?.agenzia_nome) localStorage.setItem("cc_user_agenzia_nome", me.data.agenzia_nome);
        const r = await axios.get(`${API}/user/submissions`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubs(r.data?.submissions || []);
      } catch (e) {
        if (e?.response?.status === 401) { localStorage.removeItem("cc_user_token"); nav("/agenzia/login"); return; }
        toast.error("Impossibile caricare le pratiche");
      } finally { setLoading(false); }
    })();
  }, [token, nav]);

  const logout = () => {
    ["cc_user_token", "cc_user_nome", "cc_user_email", "cc_user_account_type", "cc_user_agenzia_nome"].forEach(k => localStorage.removeItem(k));
    nav("/agenzia/login");
  };

  const filtered = filter === "all" ? subs : subs.filter(s => s.status === filter);

  // Build quick counts per status for the pill-filters
  const counts = subs.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agenzia-dashboard">
      <div className="bg-gradient-to-r from-indigo-700 to-indigo-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Building2 size={14} className="text-indigo-200" />
                <p className="text-[11px] uppercase tracking-widest text-indigo-200 font-semibold">Pannello Professionista incaricato</p>
              </div>
              <h1 className="text-3xl font-serif mt-1">{agenziaNome || "Professionista Incaricato"}</h1>
              <p className="text-sm text-indigo-200 mt-1">{email}</p>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/attestazione" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/10 border border-white/30 text-sm font-medium hover:bg-white/20" data-testid="agenzia-new-btn">
                <Home size={14} /> Nuova pratica per un cliente
              </Link>
              <button onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white/10 border border-white/30 text-sm font-medium hover:bg-white/20" data-testid="agenzia-logout-btn">
                <LogOut size={14} /> Esci
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {[
              { k: "all", lb: "Totale", v: subs.length },
              { k: "inviata", lb: "Inviate", v: counts["inviata"] || 0 },
              { k: "contratto_approvato", lb: "Firma abilitata", v: counts["contratto_approvato"] || 0 },
              { k: "approvata", lb: "Approvate", v: counts["approvata"] || 0 },
            ].map(stat => (
              <button
                key={stat.k}
                onClick={() => setFilter(stat.k)}
                className={`text-left rounded-md p-3 border transition-colors ${
                  filter === stat.k ? "bg-white text-indigo-900 border-white" : "bg-white/10 border-white/30 text-white hover:bg-white/15"
                }`}
                data-testid={`agenzia-stat-${stat.k}`}
              >
                <p className="text-[11px] uppercase tracking-wider opacity-80">{stat.lb}</p>
                <p className="text-2xl font-serif mt-0.5">{stat.v}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm">
          <div className="bg-slate-50 border-b border-slate-200 px-4 py-3 flex items-center justify-between">
            <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
              Pratiche gestite ({filtered.length}{filter !== "all" ? ` di ${subs.length}` : ""})
            </p>
            {filter !== "all" && (
              <button onClick={() => setFilter("all")} className="text-xs text-indigo-700 underline" data-testid="agenzia-clear-filter">
                Mostra tutte
              </button>
            )}
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500 text-sm"><Loader2 className="inline-block animate-spin mr-2" size={16} /> Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="agenzia-empty">
              <Inbox className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-sm text-slate-700 font-medium mb-1">
                {filter === "all" ? "Nessuna pratica trovata" : "Nessuna pratica in questo stato"}
              </p>
              <p className="text-xs text-slate-500 mb-4">
                {filter === "all"
                  ? "Le pratiche che compili per i tuoi clienti locatori appariranno qui."
                  : "Prova a cambiare filtro o crea una nuova pratica."}
              </p>
              <Link to="/attestazione" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-indigo-700 text-white text-sm font-medium hover:bg-indigo-800">
                Inizia una nuova pratica
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                  <tr>
                    <th className="px-4 py-3 text-left">Cliente locatore</th>
                    <th className="px-4 py-3 text-left">Immobile</th>
                    <th className="px-4 py-3 text-left">Conduttore</th>
                    <th className="px-4 py-3 text-left">Stato</th>
                    <th className="px-4 py-3 text-left">Data invio</th>
                    <th className="px-4 py-3 text-right">Canone</th>
                    <th className="px-4 py-3 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((s) => {
                    const st = STATUS_LABELS[s.status] || { label: s.status || "—", color: "bg-slate-100 text-slate-700 border-slate-300" };
                    return (
                      <tr key={s.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`agenzia-row-${s.id}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 flex items-center gap-1.5">
                            <UserIcon size={12} className="text-slate-400" />
                            {s.locatore_nome || <span className="text-slate-400 italic">non specificato</span>}
                          </div>
                          {s.locatore_email && (
                            <div className="text-[11px] text-slate-500 flex items-center gap-1 mt-0.5">
                              <Mail size={10} /> {s.locatore_email}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          <div className="truncate max-w-xs">{s.indirizzo || "—"}</div>
                          <div className="text-[11px] text-slate-500">{s.tipologia || "—"}{s.zona ? ` · zona ${s.zona}` : ""}</div>
                        </td>
                        <td className="px-4 py-3 text-slate-700">
                          {s.conduttore_nome || <span className="text-slate-400 italic">—</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${st.color}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-700 whitespace-nowrap text-xs">
                          {s.submitted_at ? fmtDate(s.submitted_at) : <span className="text-slate-400">bozza · {fmtDate(s.created_at)}</span>}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {s.canone_mensile ? (
                            <span className="inline-flex items-center gap-0.5 font-medium text-slate-900">
                              <Euro size={11} className="text-slate-400" />
                              {Number(s.canone_mensile).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
                              <span className="text-[10px] text-slate-400 ml-0.5">/mese</span>
                            </span>
                          ) : <span className="text-slate-400">—</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/agenzia/pratica/${s.id}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 text-xs font-medium"
                            data-testid={`agenzia-open-${s.id}`}
                          >
                            <FileText size={11} /> Apri
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
